import React, { useMemo } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { webModalCard, webModalOverlay } from '../utils/webLayout';
import { AppButton } from './AppButton';

export type AttendanceRecordDetail = {
  label: string;
  value: string;
};

type Props = {
  visible: boolean;
  title: string;
  message: string;
  details: AttendanceRecordDetail[];
  tone: 'success' | 'error';
  onClose: () => void;
};

/** Web replacement for Alert.alert, which React Native Web implements as a no-op. */
export function AttendancePunchRecordModal({
  visible,
  title,
  message,
  details,
  tone,
  onClose,
}: Props) {
  const palette = useAppColors();
  const success = tone === 'success';
  const accent = success ? palette.success : palette.error;
  const themed = useMemo(
    () => ({
      title: { ...typography.sectionTitle, color: palette.textPrimary },
      message: { ...typography.body, color: palette.textSecondary },
      label: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '600' as const,
      },
      value: { ...typography.body, color: palette.textPrimary, fontWeight: '700' as const },
    }),
    [palette],
  );

  if (!visible) return null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={webModalOverlay}>
        <Pressable style={styles.backdrop} onPress={onClose} accessibilityLabel="Dismiss" />
        <View
          accessibilityRole={success ? 'summary' : 'alert'}
          accessibilityLabel={title}
          style={[
            styles.card,
            webModalCard,
            { backgroundColor: palette.surface, borderColor: palette.border },
          ]}
        >
          <View style={[styles.badge, { backgroundColor: accent }]}>
            <Text style={{ color: success ? palette.onSuccess : palette.onError }}>
              {success ? '✓' : '!'}
            </Text>
          </View>
          <Text style={themed.title}>{title}</Text>
          <Text style={themed.message}>{message}</Text>
          {details.length > 0 ? (
            <View style={styles.details}>
              {details.map(detail => (
                <View key={detail.label} style={styles.detailRow}>
                  <Text style={themed.label}>{detail.label}</Text>
                  <Text style={themed.value}>{detail.value}</Text>
                </View>
              ))}
            </View>
          ) : null}
          <AppButton title="OK" onPress={onClose} />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { ...StyleSheet.absoluteFillObject },
  card: {
    gap: spacing.md,
    padding: spacing.xl,
    borderWidth: 1,
  },
  badge: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
  },
  details: { gap: spacing.sm },
  detailRow: { gap: spacing.xs },
});
